import { constantsData, formFieldNamesData } from "@/data";
import { optionsData } from "@/data/options";
import {
    AsyncDataKeyEnum, FieldTypeEnum, InputTypeEnum, ReconciliationStatusEnum,
} from "@/enums";

const {
    ACCOUNT,
    DATA_SOURCE,
    DATA_SOURCE_AUDIT_LOG,
    ORGANIZATION,
    ORGANIZATIONS_ENTRIES,
    RECONCILIATION,
    RECONCILIATION_AUDIT_LOG_MATCHING_TRANSACTION,
    TRANSACTION,
    TRANSACTION_AUDIT_LOG,
    TRANSACTION_VERSION_LOG,
} = constantsData.forms.filters;

const {
    accounts: accountsFieldName,
    action: actionFieldName,
    allowedDaySlip: allowedDaySlipFieldName,
    allowedDaySlipFrom: allowedDaySlipFromFieldName,
    allowedDaySlipTo: allowedDaySlipToFieldName,
    amount: amountFieldName,
    amountFrom: amountFromFieldName,
    amountTo: amountToFieldName,
    category: categoryFieldName,
    confidence: confidenceFieldName,
    confidenceFrom: confidenceFromFieldName,
    confidenceTo: confidenceToFieldName,
    connectionType: connectionTypeFieldName,
    currency: currencyFieldName,
    dataSource: dataSourceFieldName,
    dataSources: dataSourcesFieldName,
    date: dateFieldName,
    organization: organizationFieldName,
    rule: ruleFieldName,
    status: statusFieldName,
    subscriptionPlan: subscriptionPlanFieldName,
    type: typeFieldName,
    windowSize: windowSizeFieldName,
} = formFieldNamesData;

export const filtersFormData = {
    ACCOUNT: [
        {
            dataKey: AsyncDataKeyEnum.CURRENCIES,
            isAsync: true,
            label: ACCOUNT.currency.label,
            name: currencyFieldName,
            type: FieldTypeEnum.SELECT,
        },
        {
            hasOptionFormat: true,
            label: ACCOUNT.status.label,
            name: statusFieldName,
            options: optionsData.account.status.options,
            type: FieldTypeEnum.SELECT,
        },
        {
            dataKey: AsyncDataKeyEnum.CATEGORIES,
            hasOptionFormat: true,
            isAsync: true,
            label: ACCOUNT.category.label,
            name: categoryFieldName,
            type: FieldTypeEnum.SELECT,
        },
        {
            hasOptionFormat: true,
            label: ACCOUNT.type.label,
            name: typeFieldName,
            options: optionsData.balanceTypes,
            type: FieldTypeEnum.SELECT,
        },
        {
            label: ACCOUNT.date.label,
            name: dateFieldName,
            type: FieldTypeEnum.DATE_RANGE,
        },
    ],
    DATA_SOURCE: [
        {
            hasOptionFormat: true,
            label: DATA_SOURCE.status.label,
            name: statusFieldName,
            options: optionsData.dataSource.status.options,
            type: FieldTypeEnum.SELECT,
        },
        {
            dataKey: AsyncDataKeyEnum.TYPES,
            hasOptionFormat: true,
            isAsync: true,
            label: DATA_SOURCE.type.label,
            name: typeFieldName,
            type: FieldTypeEnum.MULTI_SELECT,
        },
        {
            dataKey: AsyncDataKeyEnum.CURRENCIES,
            isAsync: true,
            label: DATA_SOURCE.currency.label,
            name: currencyFieldName,
            type: FieldTypeEnum.SELECT,
        },
        {
            hasOptionFormat: true,
            label: DATA_SOURCE.connectionType.label,
            name: connectionTypeFieldName,
            options: optionsData.dataSource.connectionTypes,
            type: FieldTypeEnum.SELECT,
        },
    ],
    DATA_SOURCE_AUDIT_LOG: [
        {
            label: DATA_SOURCE_AUDIT_LOG.date.label,
            name: dateFieldName,
            type: FieldTypeEnum.DATE_RANGE,
        },
    ],
    ORGANIZATION: [
        {
            hasOptionFormat: true,
            label: ORGANIZATION.status.label,
            name: statusFieldName,
            options: optionsData.organization.status.options,
            type: FieldTypeEnum.SELECT,
        },
        {
            hasOptionFormat: true,
            label: ORGANIZATION.subscriptionPlan.label,
            name: subscriptionPlanFieldName,
            options: optionsData.organization.subscriptionPlans,
            type: FieldTypeEnum.SELECT,
        },
    ],
    ORGANIZATIONS_ENTRIES: [
        {
            label: ORGANIZATIONS_ENTRIES.date.label,
            name: dateFieldName,
            type: FieldTypeEnum.DATE_RANGE,
        },
        {
            dataKey: AsyncDataKeyEnum.ORGANIZATIONS,
            isAsync: true,
            label: ORGANIZATIONS_ENTRIES.organization.label,
            name: organizationFieldName,
            type: FieldTypeEnum.SELECT,
        },
        {
            hasOptionFormat: true,
            label: ORGANIZATIONS_ENTRIES.windowSize.label,
            name: windowSizeFieldName,
            options: optionsData.organization.windowSizes,
            type: FieldTypeEnum.TABS,
        },
    ],
    ORGANIZATION_ENTRIES: [ // eslint-disable-line
        {
            label: ORGANIZATIONS_ENTRIES.date.label,
            name: dateFieldName,
            type: FieldTypeEnum.DATE_RANGE,
        },
        {
            hasOptionFormat: true,
            label: ORGANIZATIONS_ENTRIES.windowSize.label,
            name: windowSizeFieldName,
            options: optionsData.organization.windowSizes,
            type: FieldTypeEnum.TABS,
        },
    ],
    RECONCILIATION: [
        {
            label: RECONCILIATION.date.label,
            name: dateFieldName,
            type: FieldTypeEnum.DATE_RANGE,
        },
        {
            dataKey: AsyncDataKeyEnum.DATA_SOURCES,
            isAsync: true,
            label: RECONCILIATION.dataSource.label,
            name: dataSourcesFieldName,
            type: FieldTypeEnum.MULTI_SELECT,
        },
        {
            hasOptionFormat: true,
            label: RECONCILIATION.status.label,
            name: statusFieldName,
            options: optionsData.reconciliation.job.options,
            type: FieldTypeEnum.MULTI_SELECT,
        },
    ],
    RECONCILIATION_AUDIT_LOG_MATCHING_TRANSACTION: [
        {
            hasOptionFormat: true,
            label: RECONCILIATION_AUDIT_LOG_MATCHING_TRANSACTION.status.label,
            name: statusFieldName,
            options: [ReconciliationStatusEnum.MATCHED, ReconciliationStatusEnum.REVIEW],
            type: FieldTypeEnum.SELECT,
        },
        {
            dataKey: AsyncDataKeyEnum.RULES,
            isAsync: true,
            label: RECONCILIATION_AUDIT_LOG_MATCHING_TRANSACTION.rule.label,
            name: ruleFieldName,
            type: FieldTypeEnum.SELECT,
        },
        {
            label: RECONCILIATION_AUDIT_LOG_MATCHING_TRANSACTION.date.label,
            name: dateFieldName,
            type: FieldTypeEnum.DATE_RANGE,
        },
        {
            from: {
                label: RECONCILIATION_AUDIT_LOG_MATCHING_TRANSACTION.confidence.from.label,
                name: confidenceFromFieldName,
                type: InputTypeEnum.NUMBER,
            },
            label: RECONCILIATION_AUDIT_LOG_MATCHING_TRANSACTION.confidence.label,
            max: 100,
            min: 0,
            name: confidenceFieldName,
            showPercentage: true,
            to: {
                label: RECONCILIATION_AUDIT_LOG_MATCHING_TRANSACTION.confidence.to.label,
                name: confidenceToFieldName,
                type: InputTypeEnum.NUMBER,
            },
            type: FieldTypeEnum.RANGE_SLIDER,
        },
        {
            from: {
                label: RECONCILIATION_AUDIT_LOG_MATCHING_TRANSACTION.settlementDelay.from.label,
                name: allowedDaySlipFromFieldName,
                type: InputTypeEnum.NUMBER,
            },
            label: RECONCILIATION_AUDIT_LOG_MATCHING_TRANSACTION.settlementDelay.label,
            name: allowedDaySlipFieldName,
            to: {
                label: RECONCILIATION_AUDIT_LOG_MATCHING_TRANSACTION.settlementDelay.to.label,
                name: allowedDaySlipToFieldName,
                type: InputTypeEnum.NUMBER,
            },
            type: FieldTypeEnum.RANGE_SLIDER,
        },
        {
            from: {
                label: RECONCILIATION_AUDIT_LOG_MATCHING_TRANSACTION.amount.from.label,
                name: amountFromFieldName,
                type: InputTypeEnum.NUMBER,
            },
            label: RECONCILIATION_AUDIT_LOG_MATCHING_TRANSACTION.amount.label,
            name: amountFieldName,
            to: {
                label: RECONCILIATION_AUDIT_LOG_MATCHING_TRANSACTION.amount.to.label,
                name: amountToFieldName,
                type: InputTypeEnum.NUMBER,
            },
            type: FieldTypeEnum.RANGE_SLIDER,
        },
    ],
    TRANSACTION: {
        ACCOUNT: [
            {
                dataKey: AsyncDataKeyEnum.DATA_SOURCES,
                isAsync: true,
                label: TRANSACTION.ACCOUNT.dataSource.label,
                name: dataSourceFieldName,
                type: FieldTypeEnum.SELECT,
            },
            {
                hasOptionFormat: true,
                label: TRANSACTION.ACCOUNT.status.label,
                name: statusFieldName,
                options: optionsData.reconciliation.status.options,
                type: FieldTypeEnum.MULTI_SELECT,
            },
            {
                hasOptionFormat: true,
                label: TRANSACTION.ACCOUNT.balanceType.label,
                name: typeFieldName,
                options: optionsData.balanceTypes,
                type: FieldTypeEnum.SELECT,
            },
            {
                label: TRANSACTION.ACCOUNT.dateRange.label,
                name: dateFieldName,
                type: FieldTypeEnum.DATE_RANGE,
            },
            {
                from: {
                    label: TRANSACTION.ACCOUNT.amount.from.label,
                    name: amountFromFieldName,
                    type: InputTypeEnum.NUMBER,
                },
                label: TRANSACTION.ACCOUNT.amount.label,
                name: amountFieldName,
                to: {
                    label: TRANSACTION.ACCOUNT.amount.to.label,
                    name: amountToFieldName,
                    type: InputTypeEnum.NUMBER,
                },
                type: FieldTypeEnum.RANGE_SLIDER,
            },
        ],
        DATA_SOURCE: [
            {
                hasOptionFormat: true,
                label: TRANSACTION.DATA_SOURCE.type.label,
                name: typeFieldName,
                options: optionsData.balanceTypes,
                type: FieldTypeEnum.SELECT,
            },
            {
                label: TRANSACTION.DATA_SOURCE.date.label,
                name: dateFieldName,
                type: FieldTypeEnum.DATE_RANGE,
            },
            {
                label: TRANSACTION.DATA_SOURCE.amountFrom.label,
                name: amountFromFieldName,
                type: InputTypeEnum.NUMBER,
            },
            {
                label: TRANSACTION.DATA_SOURCE.amountTo.label,
                name: amountToFieldName,
                type: InputTypeEnum.NUMBER,
            },
        ],
        ENTRY: [
            {
                hasOptionFormat: true,
                label: TRANSACTION.ENTRY.status.label,
                name: statusFieldName,
                options: optionsData.reconciliation.status.options,
                type: FieldTypeEnum.MULTI_SELECT,
            },
            {
                hasOptionFormat: true,
                label: TRANSACTION.ENTRY.type.label,
                name: typeFieldName,
                options: optionsData.balanceTypes,
                type: FieldTypeEnum.SELECT,
            },
            {
                label: TRANSACTION.ENTRY.date.label,
                name: dateFieldName,
                type: FieldTypeEnum.DATE_RANGE,
            },
            {
                from: {
                    label: TRANSACTION.ENTRY.amount.from.label,
                    name: amountFromFieldName,
                    type: InputTypeEnum.NUMBER,
                },
                label: TRANSACTION.ENTRY.amount.label,
                name: amountFieldName,
                to: {
                    label: TRANSACTION.ENTRY.amount.to.label,
                    name: amountToFieldName,
                    type: InputTypeEnum.NUMBER,
                },
                type: FieldTypeEnum.RANGE_SLIDER,
            },
        ],
        LEDGER: [
            {
                label: TRANSACTION.LEDGER.dateRange.label,
                name: dateFieldName,
                type: FieldTypeEnum.DATE_RANGE,
            },
            {
                dataKey: AsyncDataKeyEnum.ACCOUNTS,
                isAsync: true,
                label: TRANSACTION.LEDGER.accounts.label,
                name: accountsFieldName,
                type: FieldTypeEnum.MULTI_SELECT,
            },
            {
                dataKey: AsyncDataKeyEnum.DATA_SOURCES,
                isAsync: true,
                label: TRANSACTION.LEDGER.dataSources.label,
                name: dataSourcesFieldName,
                type: FieldTypeEnum.MULTI_SELECT,
            },
            {
                hasOptionFormat: true,
                label: TRANSACTION.LEDGER.status.label,
                name: statusFieldName,
                options: optionsData.reconciliation.status.options,
                type: FieldTypeEnum.MULTI_SELECT,
            },
            {
                from: {
                    label: TRANSACTION.LEDGER.amount.from.label,
                    name: amountFromFieldName,
                    type: InputTypeEnum.NUMBER,
                },
                label: TRANSACTION.LEDGER.amount.label,
                name: amountFieldName,
                to: {
                    label: TRANSACTION.LEDGER.amount.to.label,
                    name: amountToFieldName,
                    type: InputTypeEnum.NUMBER,
                },
                type: FieldTypeEnum.RANGE_SLIDER,
            },
        ],
        RECONCILIATION: [
            {
                hasOptionFormat: true,
                label: TRANSACTION.RECONCILIATION.status.label,
                name: statusFieldName,
                options: optionsData.reconciliation.status.options,
                type: FieldTypeEnum.MULTI_SELECT,
            },
            {
                hasOptionFormat: true,
                label: TRANSACTION.RECONCILIATION.type.label,
                name: typeFieldName,
                options: optionsData.balanceTypes,
                type: FieldTypeEnum.SELECT,
            },
            {
                label: TRANSACTION.RECONCILIATION.date.label,
                name: dateFieldName,
                type: FieldTypeEnum.DATE_RANGE,
            },
            {
                from: {
                    label: TRANSACTION.RECONCILIATION.amount.from.label,
                    name: amountFromFieldName,
                    type: InputTypeEnum.NUMBER,
                },
                label: TRANSACTION.RECONCILIATION.amount.label,
                name: amountFieldName,
                to: {
                    label: TRANSACTION.RECONCILIATION.amount.to.label,
                    name: amountToFieldName,
                    type: InputTypeEnum.NUMBER,
                },
                type: FieldTypeEnum.RANGE_SLIDER,
            },
        ],
    },
    TRANSACTION_AUDIT_LOG: [
        {
            dataKey: AsyncDataKeyEnum.ACTIONS,
            hasOptionFormat: true,
            isAsync: true,
            label: TRANSACTION_AUDIT_LOG.action.label,
            name: actionFieldName,
            type: FieldTypeEnum.SELECT,
        },
        {
            label: TRANSACTION_AUDIT_LOG.date.label,
            name: dateFieldName,
            type: FieldTypeEnum.DATE_RANGE,
        },
    ],
    TRANSACTION_VERSION_LOG: [
        {
            label: TRANSACTION_VERSION_LOG.date.label,
            name: dateFieldName,
            type: FieldTypeEnum.DATE_RANGE,
        },
    ],
};
